import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Lock, Shield, Eye, EyeOff, Warehouse } from 'lucide-react';
import Input from '../../components/forms/Input';
import Button from '../../components/ui/Button';
import { isValidEmail } from '../../utils/validators';
import apiService from '../../services/apiService';
import './UserForm.css';

const UserForm = ({ user, onSubmit, onCancel, isLoading, isEdit = false }) => {
    const [formData, setFormData] = useState({
        usuario: '',
        nombre: '',
        email: '',
        password: '',
        confirmPassword: '',
        telefono: '',
        rol_id: 1,
        activo: 1,
        almacenes_ids: []
    });

    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [almacenes, setAlmacenes] = useState([]);
    const [loadingAlmacenes, setLoadingAlmacenes] = useState(false);

    // Cargar roles disponibles
    useEffect(() => {
        loadRoles();
        loadAlmacenes();
    }, []);

    const loadRoles = async () => {
        try {
            setLoadingRoles(true);
            const result = await apiService.getRoles();

            if (result.success) {
                // Filtrar solo roles activos
                const activeRoles = result.data.filter(role => role.activo);
                console.log('🔍 Roles cargados:', activeRoles);
                console.log('🔍 Roles con necesita_almacen:', activeRoles.filter(r => r.necesita_almacen));
                setRoles(activeRoles);
            } else {
                console.error('Error al cargar roles:', result.message);
                // Fallback a roles básicos si falla la carga
                setRoles([
                    { id: 1, nombre: 'Administrador', necesita_almacen: false },
                    { id: 2, nombre: 'Gerente', necesita_almacen: false },
                    { id: 3, nombre: 'Cajero', necesita_almacen: false },
                    { id: 4, nombre: 'Almacenista', necesita_almacen: true }
                ]);
            }
        } catch (error) {
            console.error('Error al cargar roles:', error);
            // Fallback a roles básicos si falla la carga
            setRoles([
                { id: 1, nombre: 'Administrador', necesita_almacen: false },
                { id: 2, nombre: 'Gerente', necesita_almacen: false },
                { id: 3, nombre: 'Cajero', necesita_almacen: false },
                { id: 4, nombre: 'Almacenista', necesita_almacen: true }
            ]);
        } finally {
            setLoadingRoles(false);
        }
    };

    const loadAlmacenes = async () => {
        try {
            setLoadingAlmacenes(true);
            const result = await apiService.getAlmacenes();

            if (result.success) {
                console.log('🔍 Almacenes cargados:', result.data);
                setAlmacenes(result.data);
            } else {
                console.error('Error al cargar almacenes:', result.message);
                setAlmacenes([]);
            }
        } catch (error) {
            console.error('Error al cargar almacenes:', error);
            setAlmacenes([]);
        } finally {
            setLoadingAlmacenes(false);
        }
    };

    // Cargar datos del usuario si estamos editando
    useEffect(() => {
        if (isEdit && user) {
            setFormData({
                usuario: user.usuario || '',
                nombre: user.nombre || '',
                email: user.email || '',
                password: '',
                confirmPassword: '',
                telefono: user.telefono || '',
                rol_id: user.rol_id || 1,
                activo: user.activo || 1,
                almacenes_ids: user.almacenes_ids || []
            });
        }
    }, [isEdit, user]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar error del campo cuando el usuario empiece a escribir
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleAlmacenToggle = (almacenId) => {
        setFormData(prev => {
            const almacenes_ids = prev.almacenes_ids.includes(almacenId)
                ? prev.almacenes_ids.filter(id => id !== almacenId)
                : [...prev.almacenes_ids, almacenId];

            return { ...prev, almacenes_ids };
        });

        // Limpiar error si existe
        if (errors.almacenes_ids) {
            setErrors(prev => ({ ...prev, almacenes_ids: '' }));
        }
    };

    // Verificar si el rol seleccionado requiere almacén
    const rolRequiereAlmacen = () => {
        // Comparar ambos como string o ambos como número
        const rol = roles.find(r => String(r.id) === String(formData.rol_id));
        const requiereAlmacen = rol?.necesita_almacen || false;
        console.log('🔍 rolRequiereAlmacen:', {
            rol_id: formData.rol_id,
            roles_disponibles: roles.map(r => ({ id: r.id, tipo: typeof r.id, nombre: r.nombre })),
            rol_encontrado: rol,
            necesita_almacen: rol?.necesita_almacen,
            resultado: requiereAlmacen
        });
        return requiereAlmacen;
    };

    const validateForm = () => {
        const newErrors = {};

        // Validar usuario
        if (!formData.usuario.trim()) {
            newErrors.usuario = 'El nombre de usuario es requerido';
        } else if (formData.usuario.length < 3) {
            newErrors.usuario = 'El usuario debe tener al menos 3 caracteres';
        } else if (!/^[a-zA-Z0-9_]+$/.test(formData.usuario)) {
            newErrors.usuario = 'El usuario solo puede contener letras, números y guiones bajos';
        }

        // Validar nombre
        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre es requerido';
        } else if (formData.nombre.length < 2) {
            newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
        }

        // Validar email
        if (!formData.email.trim()) {
            newErrors.email = 'El email es requerido';
        } else if (!isValidEmail(formData.email)) {
            newErrors.email = 'El email no es válido';
        }

        // Validar password (solo requerida para crear o si se especifica en edición)
        if (!isEdit || formData.password) {
            if (!formData.password) {
                newErrors.password = 'La contraseña es requerida';
            } else if (formData.password.length < 6) {
                newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
            }

            // Validar confirmación de password
            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Las contraseñas no coinciden';
            }
        }

        // Validar teléfono (opcional pero con formato si se proporciona)
        if (formData.telefono && !/^\d{10}$/.test(formData.telefono.replace(/\D/g, ''))) {
            newErrors.telefono = 'El teléfono debe tener 10 dígitos';
        }

        // Validar rol
        if (!formData.rol_id || formData.rol_id < 1) {
            newErrors.rol_id = 'Debe seleccionar un rol';
        }

        // Validar almacenes si el rol lo requiere
        if (rolRequiereAlmacen() && (!formData.almacenes_ids || formData.almacenes_ids.length === 0)) {
            newErrors.almacenes_ids = 'Este rol requiere al menos un almacén asignado';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Preparar datos para enviar
        const dataToSubmit = {
            usuario: formData.usuario.trim(),
            nombre: formData.nombre.trim(),
            email: formData.email.trim().toLowerCase(),
            telefono: formData.telefono.replace(/\D/g, ''), // Solo números
            rol_id: parseInt(formData.rol_id),
            activo: parseInt(formData.activo),
            almacenes_ids: formData.almacenes_ids
        };

        // Solo incluir password si se proporcionó
        if (formData.password) {
            dataToSubmit.password = formData.password;
        }

        onSubmit(dataToSubmit);
    };

    const formatPhoneNumber = (value) => {
        // Remover todo excepto números
        const numbers = value.replace(/\D/g, '');

        // Formatear como XXX-XXX-XXXX
        if (numbers.length <= 3) {
            return numbers;
        } else if (numbers.length <= 6) {
            return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        } else {
            return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
        }
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        handleInputChange('telefono', formatted);
    };

    return (
        <form onSubmit={handleSubmit} className="user-form">
            <div className="form-container">
                {/* Columna Izquierda - Información Básica */}
                <div className="form-column">
                    <h3 className="column-title">
                        <User size={18} />
                        Información Básica
                    </h3>

                    {/* Usuario */}
                    <div className="form-group">
                        <Input
                            label="Nombre de Usuario"
                            type="text"
                            value={formData.usuario}
                            onChange={(e) => handleInputChange('usuario', e.target.value)}
                            error={errors.usuario}
                            icon={<User size={18} />}
                            placeholder="Ej: juan_perez"
                            required
                            disabled={isLoading || isEdit}
                        />
                        {!isEdit && (
                            <small className="form-help">
                                Solo letras, números y guiones bajos
                            </small>
                        )}
                    </div>

                    {/* Nombre completo */}
                    <div className="form-group">
                        <Input
                            label="Nombre Completo"
                            type="text"
                            value={formData.nombre}
                            onChange={(e) => handleInputChange('nombre', e.target.value)}
                            error={errors.nombre}
                            icon={<User size={18} />}
                            placeholder="Ej: Juan Pérez García"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Email */}
                    <div className="form-group">
                        <Input
                            label="Correo Electrónico"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={errors.email}
                            icon={<Mail size={18} />}
                            placeholder="juan@ejemplo.com"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Teléfono */}
                    <div className="form-group">
                        <Input
                            label="Teléfono"
                            type="tel"
                            value={formData.telefono}
                            onChange={handlePhoneChange}
                            error={errors.telefono}
                            icon={<Phone size={18} />}
                            placeholder="555-123-4567"
                            disabled={isLoading}
                            maxLength={12}
                        />
                        <small className="form-help">Opcional</small>
                    </div>
                </div>

                {/* Columna Derecha - Seguridad y Permisos */}
                <div className="form-column">
                    <h3 className="column-title">
                        <Shield size={18} />
                        Seguridad y Permisos
                    </h3>

                    {/* Rol */}
                    <div className="form-group">
                        <label className="form-label">
                            Rol del Usuario
                        </label>
                        <select
                            value={formData.rol_id}
                            onChange={(e) => handleInputChange('rol_id', e.target.value)}
                            className={`form-select ${errors.rol_id ? 'error' : ''}`}
                            disabled={isLoading || loadingRoles}
                            required
                        >
                            <option value="">
                                {loadingRoles ? 'Cargando roles...' : 'Seleccionar rol...'}
                            </option>
                            {roles.map(rol => (
                                <option key={rol.id} value={rol.id}>
                                    {rol.nombre}
                                </option>
                            ))}
                        </select>
                        {errors.rol_id && (
                            <span className="error-message">{errors.rol_id}</span>
                        )}
                        {loadingRoles && (
                            <small className="form-help">⏳ Cargando roles disponibles...</small>
                        )}
                        {!loadingRoles && roles.length === 0 && (
                            <small className="form-help">⚠️ No se pudieron cargar los roles</small>
                        )}
                    </div>

                    {/* Almacenes - Solo si el rol lo requiere */}
                    {rolRequiereAlmacen() && (
                        <div className="form-group">
                            <label className="form-label">
                                <Warehouse size={16} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                                Almacenes Asignados
                                <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                            </label>
                            <div className={`almacenes-selector required ${errors.almacenes_ids ? 'error' : ''}`}>
                                {loadingAlmacenes ? (
                                    <div className="loading-almacenes">
                                        <small>Cargando almacenes...</small>
                                    </div>
                                ) : almacenes.length === 0 ? (
                                    <div className="no-almacenes">
                                        <small>No hay almacenes disponibles</small>
                                    </div>
                                ) : (
                                    <div className="almacenes-list">
                                        {almacenes.map(almacen => (
                                            <label key={almacen.id} className="almacen-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.almacenes_ids.includes(almacen.id)}
                                                    onChange={() => handleAlmacenToggle(almacen.id)}
                                                    disabled={isLoading}
                                                />
                                                <span className="almacen-label">
                                                    {almacen.nombre}
                                                    {almacen.codigo && (
                                                        <span className="almacen-codigo"> ({almacen.codigo})</span>
                                                    )}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {errors.almacenes_ids && (
                                <span className="error-message">{errors.almacenes_ids}</span>
                            )}
                            <small className="form-help">
                                * Este rol requiere al menos un almacén asignado
                            </small>
                        </div>
                    )}

                    {/* Contraseña */}
                    <div className="form-group">
                        <label className="form-label">
                            {isEdit ? "Nueva Contraseña" : "Contraseña"}
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                className={`form-input ${errors.password ? 'error' : ''}`}
                                placeholder={isEdit ? "Dejar vacío para mantener" : "Mínimo 6 caracteres"}
                                required={!isEdit}
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="password-toggle-btn"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {errors.password && (
                            <span className="error-message">{errors.password}</span>
                        )}
                    </div>

                    {/* Confirmar contraseña */}
                    {(!isEdit || formData.password) && (
                        <div className="form-group">
                            <label className="form-label">
                                Confirmar Contraseña
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={formData.confirmPassword}
                                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                    className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                                    placeholder="Repetir contraseña"
                                    required={!isEdit || !!formData.password}
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="password-toggle-btn"
                                >
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <span className="error-message">{errors.confirmPassword}</span>
                            )}
                        </div>
                    )}

                    {/* Estado (solo para edición) */}
                    {isEdit && (
                        <div className="form-group">
                            <label className="form-label">Estado del Usuario</label>
                            <select
                                value={formData.activo}
                                onChange={(e) => handleInputChange('activo', e.target.value)}
                                className="form-select"
                                disabled={isLoading}
                            >
                                <option value={1}>✅ Activo</option>
                                <option value={0}>❌ Inactivo</option>
                            </select>
                            <small className="form-help">
                                Los usuarios inactivos no pueden acceder al sistema
                            </small>
                        </div>
                    )}
                </div>
            </div>

            {/* Botones de acción */}
            <div className="form-actions">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>

                <Button
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading}
                >
                    {isEdit ? 'Actualizar Usuario' : 'Crear Usuario'}
                </Button>
            </div>
        </form>
    );
};

export default UserForm;